import { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Loader2, Mic, X, ArrowUp, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../../lib/apiClient";
import { useAnimatedPrompt } from "./useAnimatedPrompt";
import { useVoiceTranscription } from "./useVoiceTranscription";
import {
  STARTER_PROMPTS, GUIDED_QUESTIONS, SETUP_STEPS, TRIM_PRESETS, DESIGN_THEMES,
  PUBLISH_DESTINATIONS, FRONT_BACK_MATTER, emptySetupState, combineGuidedAnswers,
  newChapterId, markFieldOrigin, normalizeBlueprint, applyBlueprintToSetup, validateBookInfo,
} from "./bookSetupDefaults";

const STARTER_KEYS = ["business", "workbook", "howto", "children", "memoir", "leadmagnet"];
const STARTER_LABELS = { business: "Business", workbook: "Workbook", howto: "How-to", children: "Children's", memoir: "Memoir", leadmagnet: "Lead magnet" };
const STRUCTURE_MODES = ["Generate with AI", "Recommended outline", "Build manually", "Import outline"];
const BOOK_TYPES = ["Business book", "How-to guide", "Workbook", "Lead magnet", "Memoir", "Children's book", "Course companion", "Journal"];
const LENGTHS = ["Short guide — 10,000 words", "Standard book — 30,000 words", "Long-form book — 50,000 words"];
const FONTS = ["Inter", "Georgia", "Merriweather", "Lora", "Source Serif 4", "Roboto"];
const PREF_KEYS = [["includeActions", "Action steps"], ["includeExamples", "Examples"], ["includeExercises", "Exercises"], ["includeSummaries", "Chapter summaries"], ["includeChecklists", "Checklists"], ["requireCitations", "Citations"]];
const DOC_KEYS = ["width", "height", "marginTop", "marginBottom", "marginInside", "marginOutside", "bleedTop", "bleedOutside", "columns", "estimatedPages"];
const PRINT_KEYS = ["binding", "readingDirection", "interiorPrinting", "paperType"];
const BP_META = [["bookType", "Type"], ["genre", "Genre"], ["targetReader", "Reader"], ["outcome", "Outcome"], ["tone", "Tone"], ["language", "Language"], ["wordCount", "Words"], ["pages", "Pages"], ["trim", "Trim"], ["destination", "Destination"], ["theme", "Theme"]];
const PATHS = [
  { id: "Create with AI", title: "Create with AI", desc: "Describe your book and generate a blueprint." },
  { id: "Guided setup", title: "Guided setup", desc: "Answer a few questions to shape your prompt." },
  { id: "Professional setup", title: "Professional setup", desc: "Configure format, design, and publishing yourself." },
  { id: "Import manuscript", title: "Import manuscript", desc: "Bring in an existing .docx, PDF, or text file." },
];

function Badge({ origin }) {
  if (!origin) return null;
  return <span className={`bl-sf-badge ${origin === "user" ? "user" : "ai"}`}>{origin === "user" ? "User edited" : "AI suggested"}</span>;
}

function Field({ label, children, origin, span }) {
  return (
    <label className={`bl-sf-field${span ? " span" : ""}`}>
      <span className="bl-sf-flabel">{label}<Badge origin={origin} /></span>
      {children}
    </label>
  );
}

function ChoiceGrid({ items, value, onPick, className }) {
  return (
    <div className={className}>
      {items.map((t) => (
        <button key={t.id || t.label} type="button" className={`bl-sf-choice${value === t.label ? " active" : ""}`} onClick={() => onPick(t)}>
          <strong>{t.label || t.title}</strong><span>{t.desc}</span>
        </button>
      ))}
    </div>
  );
}

function bookToSetup(book) {
  const base = emptySetupState();
  if (!book) return base;
  const chapters = (book.chapters || book.structure || []).map((c, i) => ({
    id: c.id || newChapterId(),
    type: c.type || "chapter",
    title: c.title || `Chapter ${i + 1}`,
    description: c.description || c.goal || c.hook || "",
    goal: c.goal || c.hook || c.description || "",
    part_title: c.part_title || null,
    order: c.order || i + 1,
    label: c.label || null,
  }));
  return {
    ...base,
    title: book.title || "",
    subtitle: book.subtitle || "",
    author: book.author || base.author,
    language: book.language || base.language,
    bookType: book.book_type || book.bookType || base.bookType,
    genre: book.genre || "",
    audience: book.audience || "",
    readerOutcome: book.goal || book.reader_outcome || "",
    tone: book.tone || base.tone,
    approximateLength: book.approximate_length || base.approximateLength,
    writingPreferences: book.writing_preferences || base.writingPreferences,
    trimSize: book.trim_size || base.trimSize,
    publishingDestination: book.publishing_destination || base.publishingDestination,
    designTheme: book.design_theme || base.designTheme,
    documentSettings: { ...base.documentSettings, ...(book.document_settings || {}) },
    printSettings: { ...base.printSettings, ...(book.print_settings || {}) },
    frontBackMatter: { ...base.frontBackMatter, ...(book.front_matter || {}) },
    blueprint: book.blueprint ? normalizeBlueprint(book.blueprint) : null,
    assumptions: book.assumptions || book.blueprint?.assumptions || [],
    chapters,
    chapterCount: chapters.length || book.target_chapters || base.chapterCount,
    creationPath: book.creation_path || base.creationPath,
    prompt: book.prompt || "",
    fieldOrigins: book.field_origins || {},
    currentStep: 1,
  };
}

export default function BookSetupFlow({ onClose, onCreated, initialDraftId, bookId, initialBook }) {
  const editMode = !!(bookId || initialBook?.id);
  const [setup, setSetup] = useState(() => (editMode ? bookToSetup(initialBook) : emptySetupState()));
  const [draftId, setDraftId] = useState(initialDraftId || null);
  const [promptFocused, setPromptFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [blueprintBusy, setBlueprintBusy] = useState(false);
  const [editingBlueprint, setEditingBlueprint] = useState(false);
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [guidedIdx, setGuidedIdx] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createdOnce, setCreatedOnce] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [toast, setToast] = useState("");
  const [error, setError] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(!!initialDraftId);
  const [importFile, setImportFile] = useState(null);
  const [importTitle, setImportTitle] = useState("");
  const [importReview, setImportReview] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(null); // null | "structure" | "generate"

  const animated = useAnimatedPrompt({ enabled: !promptFocused && !setup.prompt.trim(), reducedMotion });
  const setPrompt = useCallback((v) => setSetup((s) => ({ ...s, prompt: typeof v === "function" ? v(s.prompt) : v })), []);
  const voice = useVoiceTranscription({ value: setup.prompt, onChange: setPrompt });
  const patch = useCallback((partial) => setSetup((s) => ({ ...s, ...partial })), []);
  const setField = useCallback((key, value, { markUser } = {}) => {
    setSetup((s) => {
      const next = { ...s, [key]: value };
      if (markUser && s.fieldOrigins?.[key] === "ai") next.fieldOrigins = markFieldOrigin(s.fieldOrigins, key, "user");
      return next;
    });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    if (!initialDraftId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingDraft(true);
      try {
        const d = await api.get(`/api/book-lab/setup-drafts/${initialDraftId}`);
        if (cancelled) return;
        if (d?.setup) setSetup({ ...emptySetupState(), ...d.setup });
        setDraftId(d?.id || initialDraftId);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load draft");
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialDraftId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (guidedOpen) { setGuidedOpen(false); return; }
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [guidedOpen, onClose]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(t);
  }, [toast]);

  const step = setup.currentStep;
  const bp = setup.blueprint;
  const busy = creating || createdOnce;
  const creditEstimate = useMemo(() => bp?.estimatedCredits ?? setup.estimatedCredits ?? 18, [bp, setup.estimatedCredits]);

  const submitBlueprint = async () => {
    const prompt = setup.prompt.trim();
    if (!prompt) { setError("Enter a book description first"); return; }
    setBlueprintBusy(true); setError(null);
    try {
      const res = await api.post("/api/book-lab/blueprint", { prompt });
      const blueprint = normalizeBlueprint(res.blueprint || res);
      patch({ blueprint, estimatedCredits: blueprint.estimatedCredits ?? setup.estimatedCredits });
      setEditingBlueprint(false);
    } catch (e) {
      setError(e.message || "Blueprint generation failed");
    } finally { setBlueprintBusy(false); }
  };

  const regenerateBlueprint = async () => {
    if (Object.values(setup.fieldOrigins || {}).some((o) => o === "user")) {
      setError("Some fields were edited. Clear user edits or proceed carefully before regenerating.");
    }
    await submitBlueprint();
  };

  const applyBlueprint = () => {
    if (!bp) return;
    setSetup((s) => applyBlueprintToSetup(s, s.blueprint));
    setToast("Blueprint applied — Book Info updated");
    setError(null);
  };

  const patchBp = (key, value) => setSetup((s) => ({ ...s, blueprint: s.blueprint ? { ...s.blueprint, [key]: value } : s.blueprint }));

  const openGuided = () => { setGuidedIdx(0); setGuidedOpen(true); patch({ creationPath: "Guided setup" }); };
  const finishGuided = () => { patch({ prompt: combineGuidedAnswers(setup.guidedAnswers) }); setGuidedOpen(false); setToast("Guided answers combined into prompt"); };

  const pickPath = (pathId) => {
    if (pathId === "Guided setup") { openGuided(); return; }
    if (pathId === "Import manuscript") { document.getElementById("bl-setup-import-input")?.click(); patch({ creationPath: pathId }); return; }
    if (pathId === "Professional setup") { patch({ creationPath: pathId, currentStep: 1 }); return; }
    patch({ creationPath: pathId });
  };

  const openBlankCanvas = () => { onClose?.(); window.location.assign("/book-lab/editor?view=canvas"); };

  const onImportSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportFile(file);
    setImportTitle(file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "));
    setImportReview(true);
    patch({ creationPath: "Import manuscript" });
  };

  const confirmImport = async () => {
    if (!importFile || busy) return;
    setCreating(true); setError(null);
    try {
      const book = await api.post("/api/book-lab/books", {
        title: importTitle.trim() || "Imported manuscript",
        creation_path: "Import manuscript", language: setup.language, author: setup.author,
      });
      const form = new FormData();
      form.append("file", importFile, importFile.name);
      try { await api.upload(`/api/book-lab/books/${book.id}/import-file`, form); }
      catch (ie) { setError(ie.message || "File extraction failed — book was created without imported content."); }
      setCreatedOnce(true); setImportReview(false);
      onCreated?.(book, { generateOutline: false });
    } catch (e) { setError(e.message || "Import failed"); }
    finally { setCreating(false); }
  };

  const saveDraft = async () => {
    setSavingDraft(true); setError(null);
    try {
      const res = await api.post("/api/book-lab/setup-drafts", { ...(draftId ? { id: draftId } : {}), setup });
      if (res?.id) setDraftId(res.id);
      setToast("Draft saved");
    } catch (e) { setError(e.message || "Could not save draft"); }
    finally { setSavingDraft(false); }
  };

  const buildPayload = (generateOutline) => ({
    title: setup.title.trim(), subtitle: setup.subtitle, author: setup.author, language: setup.language,
    audience: setup.audience, tone: setup.tone, goal: setup.readerOutcome, book_type: setup.bookType,
    genre: setup.genre, creation_path: setup.creationPath, prompt: setup.prompt,
    guided_answers: setup.guidedAnswers, blueprint: setup.blueprint, field_origins: setup.fieldOrigins,
    writing_preferences: setup.writingPreferences, trim_size: setup.trimSize,
    document_settings: setup.documentSettings, publishing_destination: setup.publishingDestination,
    print_settings: setup.printSettings, design_theme: setup.designTheme,
    book_styles: {
      bodyFont: setup.bodyFont, headingFont: setup.headingFont, bodyFontSize: setup.bodyFontSize,
      lineSpacing: setup.lineSpacing, chapterOpening: setup.chapterOpening, pageNumberPosition: setup.pageNumberPosition,
    },
    front_matter: setup.frontBackMatter,
    assumptions: setup.assumptions || setup.blueprint?.assumptions || [],
    structure: setup.chapters.map((c, i) => ({
      id: c.id, order: i + 1, type: c.type || "chapter", title: c.title,
      description: c.description || c.goal || "", goal: c.goal || c.description || "",
      part_title: c.part_title || null, label: c.label || null,
    })),
    outline_chapters: setup.chapters.map((c, i) => ({
      id: c.id, order: i + 1, type: c.type || "chapter", title: c.title,
      description: c.description || c.goal || "", goal: c.goal || c.description || "",
      part_title: c.part_title || null, label: c.label || null,
    })),
    target_chapters: setup.chapters.length || setup.chapterCount || 10,
    target_words: setup.blueprint?.targetWords || undefined,
    estimated_pages: setup.documentSettings?.estimatedPages,
    approximate_length: setup.approximateLength, generate_outline: generateOutline,
  });

  const createBook = async (mode) => {
    // mode: "structure" | "generate"
    if (busy) return;
    const missing = validateBookInfo(setup);
    if (missing.length) {
      setError(`Complete Book Info first: ${missing.join(", ")}`);
      patch({ currentStep: 1 });
      return;
    }
    if (!setup.title.trim()) { setError("Title is required"); return; }
    const hasStructure = (setup.chapters || []).length > 0;
    const generateOutline = mode === "generate" && !hasStructure;
    setCreating(true); setError(null); setConfirmCreate(null);
    try {
      if (editMode) {
        const id = bookId || initialBook?.id;
        await api.patch(`/api/book-lab/books/${id}`, {
          title: setup.title.trim(),
          subtitle: setup.subtitle,
          author: setup.author,
          language: setup.language,
          audience: setup.audience,
          tone: setup.tone,
          goal: setup.readerOutcome,
          book_type: setup.bookType,
          genre: setup.genre,
          writing_preferences: setup.writingPreferences,
          trim_size: setup.trimSize,
          document_settings: setup.documentSettings,
          publishing_destination: setup.publishingDestination,
          print_settings: setup.printSettings,
          design_theme: setup.designTheme,
          book_styles: {
            bodyFont: setup.bodyFont, headingFont: setup.headingFont, bodyFontSize: setup.bodyFontSize,
            lineSpacing: setup.lineSpacing, chapterOpening: setup.chapterOpening, pageNumberPosition: setup.pageNumberPosition,
          },
          field_origins: setup.fieldOrigins,
        });
        setCreatedOnce(true);
        onCreated?.({ id, ...setup }, { generateOutline: false, editMode: true });
        setToast("Book setup saved");
        return;
      }
      const book = await api.post("/api/book-lab/books", buildPayload(generateOutline));
      if (generateOutline && book?.id) {
        try { await api.post(`/api/book-lab/books/${book.id}/generate-outline`); }
        catch (oe) { setError(oe.message || "Outline generation failed — book was still created."); }
      }
      setCreatedOnce(true);
      onCreated?.(book, { generateOutline });
    } catch (e) { setError(e.message || "Could not create book"); }
    finally { setCreating(false); }
  };

  const goNext = () => {
    if (step === 1) {
      const missing = validateBookInfo(setup);
      if (missing.length) {
        setError(`Required fields missing: ${missing.join(", ")}`);
        return;
      }
    }
    setError(null);
    patch({ currentStep: Math.min(SETUP_STEPS.length - 1, step + 1) });
  };

  const updateChapter = (idx, partial) => {
    patch({ chapters: setup.chapters.map((ch, j) => (j === idx ? { ...ch, ...partial } : ch)) });
  };
  const moveChapter = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= setup.chapters.length) return;
    const chapters = [...setup.chapters];
    [chapters[idx], chapters[j]] = [chapters[j], chapters[idx]];
    patch({ chapters });
  };
  const addChapter = () => patch({
    chapters: [...setup.chapters, { id: newChapterId(), title: `Chapter ${setup.chapters.length + 1}`, description: "", goal: "" }],
    chapterCount: setup.chapters.length + 1,
  });
  const removeChapter = (idx) => {
    const chapters = setup.chapters.filter((_, i) => i !== idx);
    patch({ chapters, chapterCount: chapters.length || setup.chapterCount });
  };
  const selectTrim = (preset) => {
    const next = { trimSize: preset.label };
    if (preset.id !== "custom") {
      const m = preset.label.match(/([\d.]+)\s*[×x]\s*([\d.]+)/i);
      if (m) next.documentSettings = { ...setup.documentSettings, width: Number(m[1]), height: Number(m[2]), units: "Inches" };
    }
    patch(next);
  };

  const gq = GUIDED_QUESTIONS[guidedIdx];
  const inp = (key, markUser) => (e) => setField(key, e.target.value, { markUser });

  const ui = (
    <div className="bl-setup-flow" data-testid="bl-setup-flow" role="dialog" aria-modal="true" aria-label="Book setup">
      <div className="bl-sf-backdrop" onClick={onClose} />
      <div className="bl-sf-panel" onClick={(e) => e.stopPropagation()}>
        <header className="bl-sf-head">
          <div><div className="bl-sf-eyebrow">Book Lab</div><h2>Create your book</h2></div>
          <button type="button" className="bl-sf-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </header>

        <div className="bl-sf-scroll">
          {loadingDraft && <div className="bl-sf-loading"><Loader2 size={18} className="bl-sf-spin" /> Loading draft…</div>}

          <section className="bl-sf-assistant">
            <div className="bl-sf-section-label">AI Book Assistant</div>
            <div className="bl-sf-prompt-wrap">
              <textarea data-testid="bl-setup-prompt" className="bl-sf-prompt" rows={5} value={setup.prompt}
                onChange={(e) => setPrompt(e.target.value)} onFocus={() => setPromptFocused(true)} onBlur={() => setPromptFocused(false)}
                aria-label="Book description prompt" />
              {!setup.prompt.trim() && !promptFocused && <div className="bl-sf-prompt-overlay" aria-hidden="true">{animated}</div>}
              <div className="bl-sf-prompt-actions">
                <button type="button" data-testid="bl-setup-mic" className={`bl-sf-icon-btn mic${voice.recording ? " recording" : ""}`}
                  onClick={voice.toggle} aria-label={voice.recording ? "Stop recording" : "Start voice transcription"} aria-pressed={voice.recording}>
                  <Mic size={16} />
                </button>
                <button type="button" data-testid="bl-setup-submit-prompt" className="bl-sf-submit" onClick={submitBlueprint}
                  disabled={blueprintBusy || !setup.prompt.trim()} aria-label="Generate blueprint">
                  {blueprintBusy ? <Loader2 size={16} className="bl-sf-spin" /> : <ArrowUp size={16} />}
                </button>
              </div>
            </div>
            <div className="bl-sf-live" aria-live="polite">{voice.status}</div>
            {voice.error === "permission" && <div className="bl-sf-err">Microphone permission denied</div>}
            <div className="bl-sf-chips">
              {STARTER_KEYS.map((k) => (
                <button key={k} type="button" className="bl-sf-chip" onClick={() => setPrompt(STARTER_PROMPTS[k])}>{STARTER_LABELS[k]}</button>
              ))}
            </div>
            <div className="bl-sf-secondary">
              <button type="button" onClick={openGuided}>Use guided questions</button>
              <button type="button" onClick={() => document.getElementById("bl-setup-import-input")?.click()}>Import manuscript</button>
              <button type="button" onClick={openBlankCanvas}>Open blank canvas</button>
              <input id="bl-setup-import-input" type="file" hidden accept=".docx,.pdf,.txt,.md,.markdown,.epub" onChange={onImportSelect} />
            </div>
          </section>

          {bp && (
            <section className="bl-sf-blueprint" data-testid="bl-setup-blueprint">
              <div className="bl-sf-bp-head">
                <h3>AI Book Blueprint</h3>
                <span className="bl-sf-credits">~{bp.estimatedCredits ?? creditEstimate} credits</span>
              </div>
              {editingBlueprint ? (
                <div className="bl-sf-grid">
                  {[["title", "Title"], ["subtitle", "Subtitle"], ...BP_META].map(([key, label]) => (
                    <Field key={key} label={label} span={key === "outcome" || key === "targetReader"}>
                      <input
                        value={key === "bookType" ? (bp.bookType ?? bp.book_type ?? "") : (bp[key] ?? "")}
                        onChange={(e) => patchBp(key === "bookType" && bp.bookType == null && bp.book_type != null ? "book_type" : key, e.target.value)}
                      />
                    </Field>
                  ))}
                  <Field label="Outline (one title per line)" span>
                    <textarea rows={5} value={(bp.outline || []).map((c) => (typeof c === "string" ? c : c.title)).join("\n")}
                      onChange={(e) => patchBp("outline", e.target.value.split("\n").filter(Boolean).map((title) => ({ title, description: "" })))} />
                  </Field>
                </div>
              ) : (
                <div className="bl-sf-bp-body">
                  <div className="bl-sf-bp-title">{bp.title || "Untitled"}</div>
                  {bp.subtitle && <div className="bl-sf-bp-sub">{bp.subtitle}</div>}
                  <dl className="bl-sf-dl">
                    {BP_META.map(([key, label]) => (
                      <div key={key}>
                        <dt>{label}</dt>
                        <dd>{bp[key] || (key === "bookType" ? bp.book_type : null) || (key === "targetReader" ? bp.audience : null) || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                  {(bp.outline || []).length > 0 && (
                    <ol className="bl-sf-outline">{(bp.outline || []).map((c, i) => <li key={i}>{typeof c === "string" ? c : c.title}</li>)}</ol>
                  )}
                  {(bp.assumptions || []).length > 0 && (
                    <div className="bl-sf-assumptions">
                      <div className="bl-sf-flabel">Assumptions</div>
                      {(bp.assumptions || []).map((a, i) => {
                        const text = typeof a === "string" ? a : a.text;
                        const accepted = typeof a === "string" ? true : a.accepted !== false;
                        return (
                          <label key={i} className="bl-sf-check">
                            <input type="checkbox" checked={accepted} onChange={() => {
                              patchBp("assumptions", bp.assumptions.map((x, j) => {
                                if (j !== i) return x;
                                if (typeof x === "string") return { text: x, accepted: false };
                                return { ...x, accepted: !(x.accepted !== false) };
                              }));
                            }} />
                            {text}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="bl-sf-bp-actions">
                <button type="button" className="bl-sf-ghost" onClick={() => { patch({ prompt: "", blueprint: null }); setEditingBlueprint(false); }}>Start over</button>
                <button type="button" className="bl-sf-ghost" onClick={regenerateBlueprint} disabled={blueprintBusy}>Regenerate</button>
                <button type="button" className="bl-sf-ghost" onClick={() => setEditingBlueprint((v) => !v)}>{editingBlueprint ? "Done editing" : "Edit Blueprint"}</button>
                <button type="button" className="bl-sf-primary" data-testid="bl-setup-apply-blueprint" onClick={applyBlueprint}>Apply Blueprint</button>
              </div>
            </section>
          )}

          {importReview && importFile && (
            <section className="bl-sf-import">
              <h3>Import manuscript</h3>
              <p className="bl-sf-muted">{importFile.name}</p>
              <Field label="Book title"><input value={importTitle} onChange={(e) => setImportTitle(e.target.value)} /></Field>
              <div className="bl-sf-row">
                <button type="button" className="bl-sf-ghost" onClick={() => { setImportReview(false); setImportFile(null); }}>Cancel</button>
                <button type="button" className="bl-sf-primary" onClick={confirmImport} disabled={busy}>
                  {creating ? <Loader2 size={14} className="bl-sf-spin" /> : null} Create from import
                </button>
              </div>
            </section>
          )}

          <nav className="bl-sf-steps" aria-label="Setup steps">
            {SETUP_STEPS.map((s, i) => (
              <button key={s.id} type="button" data-testid={`bl-setup-step-${i}`}
                className={`bl-sf-step${i === step ? " active" : ""}${i < step ? " done" : ""}`}
                onClick={() => patch({ currentStep: i })}>
                <span>{i + 1}</span>{s.label}
              </button>
            ))}
          </nav>

          <div className="bl-sf-step-body">
            {step === 0 && (
              <div className="bl-sf-paths">
                {PATHS.map((p) => (
                  <button key={p.id} type="button" className={`bl-sf-choice${setup.creationPath === p.id ? " active" : ""}`} onClick={() => pickPath(p.id)}>
                    <strong>{p.title}</strong><span>{p.desc}</span>
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="bl-sf-grid">
                <Field label="Title *" origin={setup.fieldOrigins.title}><input value={setup.title} onChange={inp("title", true)} /></Field>
                <Field label="Subtitle" origin={setup.fieldOrigins.subtitle}><input value={setup.subtitle} onChange={inp("subtitle", true)} /></Field>
                <Field label="Author"><input value={setup.author} onChange={inp("author")} /></Field>
                <Field label="Language" origin={setup.fieldOrigins.language}><input value={setup.language} onChange={inp("language", true)} /></Field>
                <Field label="Book type" origin={setup.fieldOrigins.bookType}>
                  <select value={setup.bookType} onChange={inp("bookType", true)}>{BOOK_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
                </Field>
                <Field label="Genre" origin={setup.fieldOrigins.genre}><input value={setup.genre} onChange={inp("genre", true)} /></Field>
                <Field label="Audience" origin={setup.fieldOrigins.audience} span><input value={setup.audience} onChange={inp("audience", true)} /></Field>
                <Field label="Reader outcome" origin={setup.fieldOrigins.readerOutcome} span>
                  <textarea rows={2} value={setup.readerOutcome} onChange={inp("readerOutcome", true)} />
                </Field>
                <Field label="Tone" origin={setup.fieldOrigins.tone}><input value={setup.tone} onChange={inp("tone", true)} /></Field>
                <Field label="Length" origin={setup.fieldOrigins.approximateLength}>
                  <select value={setup.approximateLength} onChange={inp("approximateLength", true)}>
                    {LENGTHS.map((t) => <option key={t}>{t}</option>)}
                    {!LENGTHS.includes(setup.approximateLength) && setup.approximateLength && <option value={setup.approximateLength}>{setup.approximateLength}</option>}
                  </select>
                </Field>
                <div className="bl-sf-prefs span">
                  <div className="bl-sf-flabel">Writing preferences</div>
                  <div className="bl-sf-pref-grid">
                    {PREF_KEYS.map(([k, label]) => (
                      <label key={k} className="bl-sf-check">
                        <input type="checkbox" checked={!!setup.writingPreferences[k]}
                          onChange={(e) => patch({ writingPreferences: { ...setup.writingPreferences, [k]: e.target.checked } })} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="bl-sf-structure">
                <div className="bl-sf-mode-row">
                  {STRUCTURE_MODES.map((m) => (
                    <button key={m} type="button" className={`bl-sf-mode${setup.structureMode === m ? " active" : ""}`} onClick={() => patch({ structureMode: m })}>{m}</button>
                  ))}
                </div>
                <Field label="Chapter count">
                  <input type="number" min={1} max={40} value={setup.chapterCount} onChange={(e) => patch({ chapterCount: Number(e.target.value) || 1 })} />
                </Field>
                <div className="bl-sf-chapters">
                  {setup.chapters.map((c, i) => (
                    <div key={c.id} className="bl-sf-chapter">
                      <input value={c.title} onChange={(e) => updateChapter(i, { title: e.target.value })} placeholder={`Chapter ${i + 1} title`} />
                      <input value={c.description || c.goal || ""} onChange={(e) => updateChapter(i, { description: e.target.value, goal: e.target.value })} placeholder="Description" />
                      <div className="bl-sf-ch-acts">
                        <button type="button" onClick={() => moveChapter(i, -1)} aria-label="Move up"><ChevronLeft size={14} /></button>
                        <button type="button" onClick={() => moveChapter(i, 1)} aria-label="Move down"><ChevronRight size={14} /></button>
                        <button type="button" onClick={() => removeChapter(i)} aria-label="Remove">×</button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="bl-sf-ghost" onClick={addChapter}><Plus size={14} /> Add chapter</button>
                </div>
                <div className="bl-sf-matter">
                  <div className="bl-sf-flabel">Front & back matter</div>
                  {FRONT_BACK_MATTER.map((m) => (
                    <label key={m.key} className="bl-sf-check">
                      <input type="checkbox" checked={!!setup.frontBackMatter[m.key]}
                        onChange={(e) => patch({ frontBackMatter: { ...setup.frontBackMatter, [m.key]: e.target.checked } })} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="bl-sf-format">
                <ChoiceGrid className="bl-sf-choice-grid" items={TRIM_PRESETS} value={setup.trimSize} onPick={selectTrim} />
                <div className="bl-sf-grid" style={{ marginTop: 12 }}>
                  {DOC_KEYS.map((k) => (
                    <Field key={k} label={k}>
                      <input type="number" step="any" value={setup.documentSettings[k] ?? ""}
                        onChange={(e) => patch({ documentSettings: { ...setup.documentSettings, [k]: Number(e.target.value) } })} />
                    </Field>
                  ))}
                  <Field label="Orientation">
                    <select value={setup.documentSettings.orientation}
                      onChange={(e) => patch({ documentSettings: { ...setup.documentSettings, orientation: e.target.value } })}>
                      <option>Portrait</option><option>Landscape</option>
                    </select>
                  </Field>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="bl-sf-design">
                <ChoiceGrid className="bl-sf-choice-grid" items={DESIGN_THEMES} value={setup.designTheme} onPick={(t) => patch({ designTheme: t.label })} />
                <div className="bl-sf-grid" style={{ marginTop: 12 }}>
                  <Field label="Body font"><select value={setup.bodyFont} onChange={(e) => patch({ bodyFont: e.target.value })}>{FONTS.map((f) => <option key={f}>{f}</option>)}</select></Field>
                  <Field label="Heading font"><select value={setup.headingFont} onChange={(e) => patch({ headingFont: e.target.value })}>{FONTS.map((f) => <option key={f}>{f}</option>)}</select></Field>
                  <Field label="Body size"><input value={setup.bodyFontSize} onChange={(e) => patch({ bodyFontSize: e.target.value })} /></Field>
                  <Field label="Line spacing"><input type="number" step="0.1" value={setup.lineSpacing} onChange={(e) => patch({ lineSpacing: Number(e.target.value) })} /></Field>
                  <Field label="Chapter opening">
                    <select value={setup.chapterOpening} onChange={(e) => patch({ chapterOpening: e.target.value })}>
                      <option>Full title page</option><option>Drop cap</option><option>Inline heading</option>
                    </select>
                  </Field>
                  <Field label="Page numbers">
                    <select value={setup.pageNumberPosition} onChange={(e) => patch({ pageNumberPosition: e.target.value })}>
                      <option>Bottom center</option><option>Bottom outside</option><option>Top outside</option><option>None</option>
                    </select>
                  </Field>
                </div>
                <div className="bl-sf-page-preview" style={{ fontFamily: setup.bodyFont, fontSize: setup.bodyFontSize, lineHeight: setup.lineSpacing }}>
                  <div className="bl-sf-page-h" style={{ fontFamily: setup.headingFont }}>{setup.title || "Chapter title"}</div>
                  <p>Sample body text in the {setup.designTheme} theme. Trim {setup.trimSize}. Page numbers: {setup.pageNumberPosition}.</p>
                  <div className="bl-sf-page-num">{setup.pageNumberPosition !== "None" ? "12" : ""}</div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="bl-sf-publish">
                <ChoiceGrid className="bl-sf-choice-grid" items={PUBLISH_DESTINATIONS} value={setup.publishingDestination} onPick={(d) => patch({ publishingDestination: d.label })} />
                <div className="bl-sf-grid" style={{ marginTop: 12 }}>
                  {PRINT_KEYS.map((k) => (
                    <Field key={k} label={k}>
                      <input value={setup.printSettings[k] || ""} onChange={(e) => patch({ printSettings: { ...setup.printSettings, [k]: e.target.value } })} />
                    </Field>
                  ))}
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="bl-sf-review">
                <dl className="bl-sf-dl">
                  {[["Title", setup.title || "—"], ["Subtitle", setup.subtitle || "—"], ["Author", setup.author],
                    ["Type", setup.bookType], ["Genre", setup.genre || "—"], ["Audience", setup.audience || "—"],
                    ["Outcome", setup.readerOutcome || "—"], ["Path", setup.creationPath],
                    ["Chapters", setup.chapters.length || setup.chapterCount],
                    ["Trim", setup.trimSize], ["Theme", setup.designTheme],
                    ["Destination", setup.publishingDestination], ["Credits", `~${creditEstimate}`],
                  ].map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
                </dl>
                {setup.chapters?.length > 0 && (
                  <ol className="bl-sf-review-outline" data-testid="bl-setup-review-outline">
                    {setup.chapters.map((c) => <li key={c.id}>{c.title}</li>)}
                  </ol>
                )}
                <label className="bl-sf-check">
                  <input type="checkbox" checked={setup.reviewPrefs.requireConfirm}
                    onChange={(e) => patch({ reviewPrefs: { ...setup.reviewPrefs, requireConfirm: e.target.checked } })} />
                  Require confirmation before create
                </label>
              </div>
            )}
          </div>

          {error && <div className="bl-sf-err" role="alert">{error}</div>}
          {toast && <div className="bl-sf-toast" role="status">{toast}</div>}
        </div>

        <footer className="bl-sf-foot">
          <button type="button" className="bl-sf-ghost" disabled={step === 0} onClick={() => patch({ currentStep: Math.max(0, step - 1) })}>Back</button>
          <button type="button" className="bl-sf-ghost" data-testid="bl-setup-save-draft" onClick={saveDraft} disabled={savingDraft}>
            {savingDraft ? <Loader2 size={14} className="bl-sf-spin" /> : null} Save as draft
          </button>
          <div className="bl-sf-foot-spacer" />
          {step < SETUP_STEPS.length - 1 ? (
            <button type="button" className="bl-sf-primary" data-testid="bl-setup-next" onClick={goNext}>Next</button>
          ) : (
            <>
              {(setup.chapters || []).length === 0 && (
                <button type="button" className="bl-sf-ghost" data-testid="bl-setup-create-outline" disabled={busy}
                  onClick={() => (setup.reviewPrefs.requireConfirm ? setConfirmCreate("generate") : createBook("generate"))}>
                  {creating ? <Loader2 size={14} className="bl-sf-spin" /> : null} Create and generate outline
                </button>
              )}
              <button type="button" className="bl-sf-primary" data-testid="bl-setup-create" disabled={busy}
                onClick={() => (setup.reviewPrefs.requireConfirm ? setConfirmCreate("structure") : createBook("structure"))}>
                {creating ? <Loader2 size={14} className="bl-sf-spin" /> : null} {editMode ? "Save book setup" : "Create book"}
              </button>
            </>
          )}
        </footer>
      </div>

      {confirmCreate && (
        <div className="bl-sf-guided" role="dialog" aria-label="Confirm create book" data-testid="bl-setup-confirm">
          <div className="bl-sf-guided-card">
            <div className="bl-sf-bp-head">
              <h3>{confirmCreate === "generate" ? "Create and generate outline?" : "Create this book?"}</h3>
              <button type="button" className="bl-sf-icon-btn" onClick={() => setConfirmCreate(null)} aria-label="Cancel"><X size={14} /></button>
            </div>
            <p className="bl-sf-muted">
              {confirmCreate === "generate"
                ? "PremierLab will create the book and ask AI to generate a chapter outline."
                : `PremierLab will create “${setup.title || "Untitled"}” with ${(setup.chapters || []).length} chapter records from your setup.`}
            </p>
            <div className="bl-sf-row">
              <button type="button" className="bl-sf-ghost" onClick={() => setConfirmCreate(null)}>Cancel</button>
              <button type="button" className="bl-sf-primary" data-testid="bl-setup-confirm-create"
                onClick={() => createBook(confirmCreate)} disabled={busy}>
                {creating ? <Loader2 size={14} className="bl-sf-spin" /> : null} Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {guidedOpen && (
        <div className="bl-sf-guided" role="dialog" aria-label="Guided questions">
          <div className="bl-sf-guided-card">
            <div className="bl-sf-bp-head">
              <h3>Guided questions</h3>
              <button type="button" className="bl-sf-icon-btn" onClick={() => setGuidedOpen(false)} aria-label="Close guided"><X size={14} /></button>
            </div>
            <p className="bl-sf-muted">{guidedIdx + 1} / {GUIDED_QUESTIONS.length}</p>
            <Field label={gq.label} span>
              {gq.type === "select" ? (
                <select value={setup.guidedAnswers[gq.key] || ""} onChange={(e) => patch({ guidedAnswers: { ...setup.guidedAnswers, [gq.key]: e.target.value } })}>
                  <option value="">Select…</option>
                  {(gq.options || []).map((o) => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <textarea rows={4} placeholder={gq.placeholder} value={setup.guidedAnswers[gq.key] || ""}
                  onChange={(e) => patch({ guidedAnswers: { ...setup.guidedAnswers, [gq.key]: e.target.value } })} />
              )}
            </Field>
            <div className="bl-sf-row">
              <button type="button" className="bl-sf-ghost" disabled={guidedIdx === 0} onClick={() => setGuidedIdx((i) => Math.max(0, i - 1))}>Back</button>
              <button type="button" className="bl-sf-ghost" onClick={() => { if (guidedIdx >= GUIDED_QUESTIONS.length - 1) finishGuided(); else setGuidedIdx((i) => i + 1); }}>Skip</button>
              {guidedIdx >= GUIDED_QUESTIONS.length - 1
                ? <button type="button" className="bl-sf-primary" onClick={finishGuided}>Finish</button>
                : <button type="button" className="bl-sf-primary" onClick={() => setGuidedIdx((i) => i + 1)}>Next</button>}
            </div>
          </div>
        </div>
      )}

      <style>{CSS}</style>
    </div>
  );

  if (typeof document === "undefined") return ui;
  return createPortal(ui, document.body);
}

const CSS = `
.bl-setup-flow{position:fixed;inset:0;z-index:91000;display:grid;place-items:center;font-family:Inter,system-ui,sans-serif;color:#E6EFEA}
.bl-sf-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.62);backdrop-filter:blur(8px)}
.bl-sf-panel{position:relative;width:min(920px,96vw);max-height:94vh;display:flex;flex-direction:column;background:#071414;border:1px solid rgba(255,255,255,.08);border-radius:16px;overflow:hidden;box-shadow:0 28px 80px rgba(0,0,0,.55)}
.bl-sf-head{display:flex;align-items:center;gap:12px;padding:16px 20px;border-bottom:1px solid rgba(255,255,255,.06)}
.bl-sf-head h2{margin:2px 0 0;font-size:18px;font-weight:800}
.bl-sf-eyebrow{font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:#7DE4B1}
.bl-sf-scroll{flex:1;overflow-y:auto;padding:18px 20px 28px}
.bl-sf-icon-btn{margin-left:auto;height:32px;width:32px;border-radius:8px;display:grid;place-items:center;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.06);color:#E6EFEA;cursor:pointer}
.bl-sf-icon-btn.mic.recording{color:#04150E;background:#41E19A;border-color:#41E19A;animation:bl-sf-pulse 1.1s ease infinite}
@keyframes bl-sf-pulse{0%,100%{box-shadow:0 0 0 0 rgba(65,225,154,.45)}50%{box-shadow:0 0 0 8px rgba(65,225,154,0)}}
.bl-sf-spin{animation:bl-sf-spin .8s linear infinite}@keyframes bl-sf-spin{to{transform:rotate(360deg)}}
.bl-sf-section-label{font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#7DE4B1;margin-bottom:10px}
.bl-sf-assistant{margin-bottom:18px}
.bl-sf-prompt-wrap{position:relative}
.bl-sf-prompt{width:100%;min-height:120px;resize:vertical;padding:14px 52px 48px 14px;border-radius:12px;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);color:#E6EFEA;font:inherit;font-size:14px;outline:0;box-sizing:border-box}
.bl-sf-prompt:focus{border-color:rgba(65,225,154,.5)}
.bl-sf-prompt-overlay{position:absolute;left:14px;top:14px;right:52px;pointer-events:none;color:#6E7C77;font-size:14px;line-height:1.45;white-space:pre-wrap}
.bl-sf-prompt-actions{position:absolute;right:10px;bottom:10px;display:flex;gap:6px}
.bl-sf-submit{height:34px;width:34px;border-radius:10px;border:0;cursor:pointer;display:grid;place-items:center;background:linear-gradient(135deg,#1FB870,#41E19A);color:#04150E}
.bl-sf-submit:disabled{opacity:.45;cursor:not-allowed}
.bl-sf-live{min-height:16px;margin-top:6px;font-size:11.5px;color:#7DE4B1}
.bl-sf-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}
.bl-sf-chip{padding:6px 10px;border-radius:999px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);color:#A5F0CE;font-size:11.5px;cursor:pointer}
.bl-sf-chip:hover{border-color:rgba(125,228,177,.35)}
.bl-sf-secondary{display:flex;flex-wrap:wrap;gap:10px;margin-top:12px}
.bl-sf-secondary button{background:none;border:0;color:#7DE4B1;font-size:12.5px;cursor:pointer;text-decoration:underline;padding:0}
.bl-sf-blueprint,.bl-sf-import{margin:16px 0;padding:14px;border-radius:12px;border:1px solid rgba(125,228,177,.18);background:rgba(65,225,154,.05)}
.bl-sf-review-outline{margin:12px 0 0;padding-left:20px;color:#C8D6D0;line-height:1.6;font-size:13px}
.bl-sf-bp-head{display:flex;align-items:center;gap:10px}.bl-sf-bp-head h3{margin:0;font-size:15px;font-weight:800}
.bl-sf-credits{margin-left:auto;font-size:11px;color:#7DE4B1}
.bl-sf-bp-title{margin-top:10px;font-size:20px;font-weight:800}.bl-sf-bp-sub{color:#9BABA6;font-size:13px;margin-top:2px}
.bl-sf-dl{display:grid;grid-template-columns:1fr 1fr;gap:8px 14px;margin:12px 0 0}
.bl-sf-dl div{display:flex;flex-direction:column;gap:2px}
.bl-sf-dl dt{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#7B8A85}.bl-sf-dl dd{margin:0;font-size:13px}
.bl-sf-outline{margin:12px 0 0;padding-left:18px;font-size:13px;color:#C8D6D0}
.bl-sf-assumptions{margin-top:12px;display:flex;flex-direction:column;gap:6px}
.bl-sf-bp-actions,.bl-sf-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px;align-items:center}
.bl-sf-primary{display:inline-flex;align-items:center;gap:6px;padding:9px 14px;border:0;border-radius:10px;background:linear-gradient(135deg,#1FB870,#41E19A);color:#04150E;font-weight:700;font-size:12.5px;cursor:pointer}
.bl-sf-primary:disabled,.bl-sf-ghost:disabled{opacity:.45;cursor:not-allowed}
.bl-sf-ghost{display:inline-flex;align-items:center;gap:6px;padding:9px 12px;border-radius:10px;cursor:pointer;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:#E6EFEA;font-size:12.5px}
.bl-sf-steps{display:flex;flex-wrap:wrap;gap:6px;margin:18px 0 14px}
.bl-sf-step{display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border-radius:8px;border:1px solid rgba(255,255,255,.06);background:transparent;color:#7B8A85;font-size:11.5px;cursor:pointer}
.bl-sf-step span{width:18px;height:18px;border-radius:50%;display:grid;place-items:center;font-size:10px;background:rgba(255,255,255,.06)}
.bl-sf-step.active{color:#7DE4B1;border-color:rgba(125,228,177,.35);background:rgba(65,225,154,.08)}
.bl-sf-step.done span{background:rgba(65,225,154,.25);color:#04150E}
.bl-sf-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.bl-sf-field{display:flex;flex-direction:column;gap:4px}.bl-sf-field.span,.bl-sf-prefs.span{grid-column:1/-1}
.bl-sf-flabel{font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;color:#7B8A85;display:flex;align-items:center;gap:6px}
.bl-sf-field input,.bl-sf-field select,.bl-sf-field textarea,.bl-sf-chapter input{padding:9px 11px;border-radius:8px;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);color:#E6EFEA;font-size:13px;outline:0;font-family:inherit;width:100%;box-sizing:border-box}
.bl-sf-field input:focus,.bl-sf-field select:focus,.bl-sf-field textarea:focus{border-color:rgba(65,225,154,.5)}
.bl-sf-badge{font-size:9px;letter-spacing:.08em;text-transform:uppercase;padding:2px 6px;border-radius:6px}
.bl-sf-badge.ai{background:rgba(65,225,154,.15);color:#7DE4B1}.bl-sf-badge.user{background:rgba(120,197,255,.15);color:#78C5FF}
.bl-sf-check{display:flex;align-items:flex-start;gap:8px;font-size:12.5px;color:#C8D6D0;cursor:pointer}
.bl-sf-pref-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px}
.bl-sf-paths,.bl-sf-choice-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.bl-sf-choice{text-align:left;padding:14px;border-radius:12px;cursor:pointer;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);color:#E6EFEA;display:flex;flex-direction:column;gap:4px}
.bl-sf-choice strong{font-size:13.5px}.bl-sf-choice span{font-size:12px;color:#7B8A85;line-height:1.4}
.bl-sf-choice.active,.bl-sf-mode.active{border-color:rgba(125,228,177,.4);background:rgba(65,225,154,.1)}
.bl-sf-mode-row{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px}
.bl-sf-mode{padding:8px 12px;border-radius:8px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);color:#E6EFEA;font-size:12px;cursor:pointer}
.bl-sf-chapters{display:flex;flex-direction:column;gap:8px;margin:12px 0}
.bl-sf-chapter{display:grid;grid-template-columns:1.2fr 1.4fr auto;gap:6px;align-items:center}
.bl-sf-ch-acts{display:flex;gap:4px}
.bl-sf-ch-acts button{height:28px;width:28px;border-radius:6px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);color:#E6EFEA;cursor:pointer}
.bl-sf-matter{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px}
.bl-sf-page-preview{margin-top:16px;padding:28px 24px 36px;border-radius:4px;background:#F7F4EE;color:#1a1a1a;min-height:180px;position:relative;box-shadow:0 8px 24px rgba(0,0,0,.25)}
.bl-sf-page-h{font-size:1.35em;font-weight:700;margin-bottom:10px}
.bl-sf-page-num{position:absolute;bottom:10px;left:0;right:0;text-align:center;font-size:11px;color:#666}
.bl-sf-foot{display:flex;align-items:center;gap:8px;padding:12px 20px;border-top:1px solid rgba(255,255,255,.06);background:#0A1818}
.bl-sf-foot-spacer{flex:1}
.bl-sf-err{margin-top:12px;padding:8px 10px;border-radius:8px;background:rgba(220,38,38,.12);color:#FCA5A5;font-size:12px}
.bl-sf-toast{margin-top:10px;padding:8px 10px;border-radius:8px;background:rgba(65,225,154,.12);color:#A5F0CE;font-size:12px}
.bl-sf-muted{color:#7B8A85;font-size:12.5px}
.bl-sf-loading{display:flex;align-items:center;gap:8px;color:#7DE4B1;font-size:13px;margin-bottom:12px}
.bl-sf-guided{position:absolute;inset:0;z-index:2;display:grid;place-items:center;background:rgba(0,0,0,.45);padding:20px}
.bl-sf-guided-card{width:min(480px,100%);background:#0B1B1B;border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:18px}
@media (max-width:720px){.bl-sf-grid,.bl-sf-paths,.bl-sf-choice-grid,.bl-sf-dl,.bl-sf-pref-grid,.bl-sf-matter{grid-template-columns:1fr}.bl-sf-chapter{grid-template-columns:1fr}}
`;
